// Platform configuration
// The operator's Nostr pubkey
export const OPERATOR_PUBKEY = 'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc';

// Dedicated key used by the automated verification backend to sign NIP-05
// grant events (kind 30078, d-tag `bchnostr/nip05-override/<name>`). It is a
// SEPARATE key from the operator so the operator's fund-holding nsec never
// touches a server. Its nsec lives only in the Netlify env var GRANT_NSEC.
// Grants signed by EITHER this key or OPERATOR_PUBKEY are honoured.
export const GRANT_PUBKEY = 'cc71a57c6417efdbc9e4966472f1199911007e96b09420763a9bd1687e300862';

/** Pubkeys allowed to issue verification grants (operator + backend key). */
export const GRANT_ISSUERS = [OPERATOR_PUBKEY, GRANT_PUBKEY];

/** Is this pubkey allowed to issue verification grants? */
export function isGrantIssuer(pubkey: string | undefined | null): boolean {
  if (!pubkey || typeof pubkey !== 'string') return false;
  return GRANT_ISSUERS.includes(pubkey.trim().toLowerCase());
}

/**
 * Is this pubkey the operator?
 *
 * Compares case-insensitively and ignores stray whitespace. Signers and
 * bunkers differ in how they hand back a pubkey — some upper-case it —
 * and a raw `===` against the lower-case constant silently hides every
 * operator-only tool with no clue as to why. Always use this helper.
 */
export function isOperatorPubkey(pubkey: string | undefined | null): boolean {
  if (!pubkey || typeof pubkey !== 'string') return false;
  return pubkey.trim().toLowerCase() === OPERATOR_PUBKEY;
}

// Operator's BCH address — receives 1% platform fee on every tip/payment
export const PLATFORM_FEE_ADDRESS = 'bitcoincash:qzfczm3kc9j8aw0pjm5azym8s8xg07ssru5yr5qepj';

// Platform fee: 1% of all tips and payments
export const PLATFORM_FEE_PERCENT = 0.01; // 1%

// PUSD (CashToken) platform fee: 1% of the tip's USD value, but COLLECTED IN
// BCH rather than PUSD. PUSD has only 2 decimals ($0.01 floor), so a 1% PUSD
// fee would round to zero on any sub-$1 tip. Taking the fee in BCH (8
// decimals) means even a $0.05 micro-tip yields a real, collectable fee.
export const PUSD_FEE_PERCENT = 0.01; // 1%

/**
 * Platform fee for a PUSD tip, returned in SATOSHIS (BCH).
 *
 * @param usdAmount  The tip's dollar value (PUSD ≈ $1, so this is just the
 *                   PUSD amount in dollars).
 * @param bchPriceUsd  Current BCH price in USD (from the live price feed).
 * @returns Fee in satoshis, or 0n if it would be below the BCH dust limit
 *          (in which case the caller should omit the fee output — the amount
 *          is too small to be worth a separate on-chain output).
 */
export function calculatePusdFeeSats(usdAmount: number, bchPriceUsd: number): bigint {
  if (!Number.isFinite(usdAmount) || usdAmount <= 0) return BigInt(0);
  if (!Number.isFinite(bchPriceUsd) || bchPriceUsd <= 0) return BigInt(0);
  const feeUsd = usdAmount * PUSD_FEE_PERCENT;
  const feeSats = Math.round((feeUsd / bchPriceUsd) * 1e8);
  if (feeSats < BCH_DUST_LIMIT) return BigInt(0); // below dust — not worth an output
  return BigInt(feeSats);
}

// BCH dust limit — outputs below this are rejected by the network
export const BCH_DUST_LIMIT = 546;

// ── Fee Calculation ───────────────────────────────────────────────
//
// Fee is ALWAYS calculated as a percentage of the settled amount.
// It scales linearly: small tip → small fee, large tip → large fee.
//
// The fee is DEDUCTED from the payment — not added on top.
//   User sends:   amountSats (total)
//   Recipient:    amountSats - fee
//   Platform:     fee (as separate output if >= dust, else implicit)
//
// If the fee is below BCH dust limit (546), it CANNOT be a separate
// tx output. In that case, sendTipWithFee deducts it from the
// recipient output anyway — the fee becomes implicit (absorbed as
// extra miner fee / retained value). The platform still "earns" it
// because the recipient receives less.

/** Calculate platform fee for a settled payment amount. Always proportional. */
export function calculatePlatformFee(amountSats: number): number {
  if (amountSats <= 0) return 0;
  return Math.ceil(amountSats * PLATFORM_FEE_PERCENT);
}

/** Whether the fee is large enough to be a separate BCH tx output */
export function canSendFeeOutput(feeSats: number): boolean {
  return feeSats >= BCH_DUST_LIMIT;
}

/** Calculate breakdown for a payment amount (fee deducted from amount) */
export function feeBreakdown(amountSats: number): {
  total: number;       // what the user spends (= amountSats)
  fee: number;         // platform fee (always proportional)
  net: number;         // what recipient actually receives
  feeAsOutput: boolean; // whether fee can be a separate tx output
} {
  const fee = calculatePlatformFee(amountSats);
  return {
    total: amountSats,
    fee,
    net: amountSats - fee,
    feeAsOutput: canSendFeeOutput(fee),
  };
}

/** @deprecated Use feeBreakdown instead */
export function totalWithFee(amountSats: number): { tip: number; fee: number; total: number } {
  const fee = calculatePlatformFee(amountSats);
  return { tip: amountSats, fee, total: amountSats };
}

// ── Data Saver Mode ──────────────────────────────────────────────
// Reduces data usage for users on limited/expensive mobile plans.
// When enabled:
//   - Images lazy-load only when scrolled into view
//   - Profile pictures in feeds are replaced with placeholders
//   - Fewer relay connections (3 instead of 7)
//   - Lower query limits (less historical data fetched)
//   - Dashboard/analytics queries deferred until explicitly requested
//   - Videos/GIFs don't autoplay

const DATA_SAVER_KEY = 'bchnostr:data-saver';

/** Check if Data Saver mode is enabled. Synchronous, cheap. */
export function isDataSaverEnabled(): boolean {
  try { return localStorage.getItem(DATA_SAVER_KEY) === '1'; } catch { return false; }
}

/** Toggle Data Saver mode on/off. */
export function setDataSaver(enabled: boolean) {
  try { localStorage.setItem(DATA_SAVER_KEY, enabled ? '1' : '0'); } catch {}
}

/** Max relays to use for queries. Data Saver = 2, normal = all. */
export function getMaxRelays(): number {
  return isDataSaverEnabled() ? 2 : Infinity;
}

